/**
 * Sessions List Component
 * Displays a list of user's AI assist sessions on the right side of the page
 */

import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Divider,
  Card,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Add as AddIcon, MoreVert as MoreVertIcon, ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import { 
  listSessions, 
  getShortSessionId, 
  storeSessionId,
  updateSessionDisplayName,
  deleteSession,
  type SessionInfo,
} from '@flowrithm/shared/src/features/ai-assist/services/sessionService';

interface SessionsListProps {
  currentSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
  isCreatingSession?: boolean;
  width?: number;
}

export interface SessionsListRef {
  refresh: () => void;
}

export const SessionsList = forwardRef<SessionsListRef, SessionsListProps>(
  ({ currentSessionId, onSessionSelect, onNewChat, isCreatingSession = false, width = 250 }, ref) => {
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [renameValue, setRenameValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);

  const loadSessions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await listSessions();
      // Handle both old format (string[]) and new format (SessionInfo[])
      const sessionsData = response.sessions || [];
      const normalizedSessions: SessionInfo[] = sessionsData.map((session: SessionInfo | string) => {
        // If it's a string (old format), convert to SessionInfo
        if (typeof session === 'string') {
          return {
            session_id: session,
            display_name: null,
          };
        }
        // If it's already SessionInfo, ensure it has required fields
        return {
          session_id: session.session_id || '',
          display_name: session.display_name || null,
        };
      }).filter((session: SessionInfo) => session.session_id); // Filter out invalid sessions
      setSessions(normalizedSessions);
    } catch (err) {
      console.error('[SessionsList] Failed to load sessions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, sessionId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setMenuAnchor(event.currentTarget);
    setSelectedSession(sessionId);
    const session = sessions.find(s => s.session_id === sessionId);
    setRenameValue(session?.display_name || '');
    // Keep hover state when menu opens
    setHoveredSessionId(sessionId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    // Don't clear selectedSession here - it's needed for rename/delete operations
  };

  const handleRenameClick = () => {
    setRenameDialogOpen(true);
    setMenuAnchor(null); // Close menu but keep selectedSession
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setMenuAnchor(null); // Close menu but keep selectedSession
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSession) return;

    try {
      setIsDeleting(true);
      await deleteSession(selectedSession);
      await loadSessions();
      
      // If deleted session was current, clear it
      if (selectedSession === currentSessionId) {
        onSessionSelect(''); // Clear current session
      }
      setDeleteDialogOpen(false);
      setSelectedSession(null);
    } catch (err) {
      console.error('[SessionsList] Failed to delete session:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete session. Please try again.';
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
      // Keep delete dialog open so user can retry or cancel
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRenameSubmit = async () => {
    if (!selectedSession || !renameValue.trim()) return;

    try {
      setIsRenaming(true);
      await updateSessionDisplayName(selectedSession, renameValue.trim());
      await loadSessions();
      // Close dialog and reset state on success
      setRenameDialogOpen(false);
      setSelectedSession(null);
      setRenameValue('');
    } catch (err) {
      console.error('[SessionsList] Failed to rename session:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to rename session. Please try again.';
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
      // Keep dialog open on error so user can retry or cancel
    } finally {
      setIsRenaming(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  // Expose refresh function via ref
  useImperativeHandle(ref, () => ({
    refresh: loadSessions,
  }));

  const handleSessionClick = (sessionId: string) => {
    // Store the selected session
    storeSessionId(sessionId, true);
    // Notify parent component
    onSessionSelect(sessionId);
    // Reload sessions to update the list
    loadSessions();
  };

  return (
    <>
    <Card
      sx={{
        width: width,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'rgba(4, 55, 123, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        boxShadow: `
          0 0 0 1px rgba(4, 55, 123, 0.15),
          0 0 8px rgba(4, 55, 123, 0.2),
          0 0 15px rgba(4, 55, 123, 0.15),
          inset 0 0 10px rgba(255, 255, 255, 0.03),
          inset 0 1px 0 rgba(255, 255, 255, 0.1),
          2px 0 10px rgba(4, 55, 123, 0.15),
          -2px 0 10px rgba(4, 55, 123, 0.1)
        `,
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'rgba(0, 0, 0, 0.12)',
          backgroundColor: 'transparent',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ color: '#04377b', fontWeight: 600, fontSize: '1rem' }}>
            Sessions
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={isCreatingSession ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={onNewChat}
            disabled={isCreatingSession}
            sx={{
              bgcolor: 'rgba(4, 55, 123, 0.6)',
              color: '#fff',
              textTransform: 'none',
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              '&:hover': {
                bgcolor: 'rgba(4, 55, 123, 0.75)',
              },
              '&:disabled': {
                bgcolor: 'rgba(4, 55, 123, 0.3)',
              },
            }}
          >
            {isCreatingSession ? 'Creating...' : 'New Chat'}
          </Button>
        </Box>
        {sessions.length > 0 && (
          <Typography variant="caption" sx={{ color: 'rgba(4, 55, 123, 0.6)', fontSize: '0.7rem' }}>
            {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
          </Typography>
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: 'transparent',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(4, 55, 123, 0.2)',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: 'rgba(4, 55, 123, 0.3)',
            },
          },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem' }}>
              {error}
            </Typography>
          </Box>
        ) : sessions.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(4, 55, 123, 0.6)', fontSize: '0.75rem' }}>
              No sessions yet. Start a new chat to create one.
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {sessions.map((session, index) => {
              // Defensive check for session_id
              if (!session || !session.session_id) {
                return null;
              }
              const shortId = getShortSessionId(session.session_id);
              const displayName = session.display_name || shortId;
              const isSelected = currentSessionId === session.session_id;
              
              const isHovered = hoveredSessionId === session.session_id;
              const isMenuOpen = menuAnchor !== null && selectedSession === session.session_id;
              const showMenuButton = isHovered || isMenuOpen;
              
              return (
                <Box 
                  key={session.session_id}
                  onMouseEnter={() => setHoveredSessionId(session.session_id)}
                  onMouseLeave={() => {
                    // Don't clear hover if menu is open for this session
                    if (!isMenuOpen) {
                      setHoveredSessionId(null);
                    }
                  }}
                >
                  <ListItem 
                    disablePadding
                    secondaryAction={
                      showMenuButton ? (
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => handleMenuOpen(e, session.session_id)}
                          sx={{ color: 'rgba(4, 55, 123, 0.6)' }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      ) : null
                    }
                  >
                    <ListItemButton
                      onClick={() => handleSessionClick(session.session_id)}
                      selected={isSelected}
                      sx={{
                        py: 0.5,
                        px: 2,
                        pr: showMenuButton ? 6 : 2, // Add padding for menu button when visible
                        position: 'relative',
                        borderLeft: `3px solid ${isSelected ? 'primary.main' : 'primary.light'}`,
                        backgroundColor: isSelected 
                          ? 'rgba(25, 118, 210, 0.15)' 
                          : 'rgba(25, 118, 210, 0.08)',
                        '&:hover': {
                          backgroundColor: isSelected 
                            ? 'rgba(25, 118, 210, 0.2)' 
                            : 'rgba(25, 118, 210, 0.12)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.2)',
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.25)',
                          },
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: isSelected ? 'bold' : 'normal',
                              fontSize: '0.8rem',
                              color: isSelected ? 'primary.main' : 'primary.light',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {displayName.length > 25 ? `${displayName.substring(0, 25)}...` : displayName}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < sessions.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        )}
      </Box>

      {/* Menu for session options */}
      {menuAnchor && (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          MenuListProps={{
            'aria-labelledby': 'session-options-menu',
          }}
          PaperProps={{
            sx: {
              mt: 0.5,
              minWidth: 180,
              zIndex: 1300, // Ensure menu appears above other elements
            },
          }}
          disablePortal={false}
        >
          <MenuItem 
            onClick={handleRenameClick}
            sx={{ fontSize: '0.875rem' }}
          >
            Rename session
          </MenuItem>
          <MenuItem 
            onClick={handleDeleteClick} 
            disabled={isDeleting}
            sx={{ fontSize: '0.875rem' }}
          >
            {isDeleting ? 'Deleting...' : 'Delete session'}
          </MenuItem>
        </Menu>
      )}

      {/* Rename Dialog */}
      <Dialog 
        open={renameDialogOpen} 
        onClose={() => {
          if (!isRenaming) {
            setRenameDialogOpen(false);
            setSelectedSession(null);
            setRenameValue('');
          }
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Rename Session</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Session Name"
            fullWidth
            variant="outlined"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isRenaming && renameValue.trim()) {
                handleRenameSubmit();
              }
            }}
            disabled={isRenaming}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setRenameDialogOpen(false);
              setSelectedSession(null);
              setRenameValue('');
            }} 
            disabled={isRenaming}
          >
            Cancel
          </Button>
          <Button onClick={handleRenameSubmit} variant="contained" disabled={isRenaming || !renameValue.trim()}>
            {isRenaming ? 'Renaming...' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => !isDeleting && setDeleteDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 600 }}>
          Delete Session
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this session?
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This action cannot be undone. All messages and data associated with this session will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={isDeleting}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            disabled={isDeleting}
            sx={{
              bgcolor: '#d32f2f',
              color: '#fff',
              '&:hover': {
                bgcolor: '#b71c1c',
              },
              '&:disabled': {
                bgcolor: 'rgba(211, 47, 47, 0.3)',
              },
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog 
        open={errorDialogOpen} 
        onClose={() => setErrorDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ErrorOutlineIcon sx={{ fontSize: 28, color: '#d32f2f' }} />
          Error
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {errorMessage || 'An unexpected error occurred. Please try again.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setErrorDialogOpen(false)} 
            variant="contained"
            sx={{
              bgcolor: '#d32f2f',
              color: '#fff',
              '&:hover': {
                bgcolor: '#b71c1c',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
    </>
  );
  }
);

